import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { IItem } from '@blink/components';
import { ReactComponent as MagnifyingGlassIcon } from '@blink/assets/src/icons/magnifyingGlass.svg';
import { ReactComponent as MailIcon } from '@blink/assets/src/icons/mail.svg';

import { TeamSectionRow } from '../../TeamSectionRow';
import { TeamSkeletonRow } from '../../TeamSectionRow/ui/TeamSkeletonRow';

import { STATUS } from '@/features/Team/ui/TeamSection/hooks/useTeamSection';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { Member } from '@/shared/api/organization';
import {
    Button,
    ButtonGroup,
    ButtonVariant,
    Checkbox,
    Input,
    TooltipWrapperDisabled,
} from '@/shared/ui';

const statusItems = [
    { text: 'All Users', value: STATUS.ALL },
    { text: 'Active Users', value: STATUS.ACTIVE },
    { text: 'Pending Invites', value: STATUS.PENDING },
] as Array<IItem>;

type TeamSectionProps = {
    organizationMembersLoading: boolean;
    filteredMembers: Member[];
    selectedMembers: Member[];
    setSelectedMembers: React.Dispatch<React.SetStateAction<Member[]>>;
    searchTerm: string;
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
    statusFilter: STATUS;
    setStatusFilter: React.Dispatch<React.SetStateAction<STATUS>>;
    disableOperations?: boolean;
};

export const TeamSection: FC<TeamSectionProps> = ({
    organizationMembersLoading,
    filteredMembers,
    selectedMembers,
    setSelectedMembers,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    disableOperations = false,
}) => {
    const dispatch = useDispatch();

    const handleSelectAll = () => {
        const allSelected = filteredMembers.every((member) =>
            selectedMembers.some((selected) => selected.email === member.email),
        );

        setSelectedMembers(
            allSelected
                ? selectedMembers.filter(
                      (member) =>
                          !filteredMembers.some((filtered) => filtered.email === member.email),
                  )
                : [
                      ...selectedMembers,
                      ...filteredMembers.filter(
                          (member) =>
                              !selectedMembers.some((selected) => selected.email === member.email),
                      ),
                  ],
        );
    };

    const handleSelectItem = (member: Member) => {
        setSelectedMembers((prevSelected) =>
            prevSelected.some((prev) => prev.email === member.email)
                ? prevSelected.filter((prev) => prev.email !== member.email)
                : [...prevSelected, member],
        );
    };

    const handleStatusChange = (status: any) => {
        setStatusFilter(status);
    };

    const handleResendInvites = () => {
        dispatch(
            showModalAction({
                modalName: ModalName.RESEND_SELECTED,
                modalTitle: 'Resend Invites',
                modalProps: {
                    invites: selectedMembers,
                    note: 'Are you sure you want to resend invites to the following users?',
                },
            }),
        );
    };

    const getContent = () => {
        if (organizationMembersLoading) {
            return Array.from({ length: 6 }).map((_, index) => <TeamSkeletonRow key={index} />);
        }
        return filteredMembers.map((member, index) => (
            <TeamSectionRow
                key={index}
                member={member}
                isSelected={selectedMembers.includes(member)}
                onSelect={handleSelectItem}
                disableOperations={disableOperations}
            />
        ));
    };

    const isAllSelected = () =>
        filteredMembers.length > 0 &&
        filteredMembers.every((member) =>
            selectedMembers.some((selected) => selected.email === member.email),
        );

    return (
        <div className='w-full text-primary'>
            <div className='flex justify-between items-center mb-[var(--spacing-07)]'>
                <ButtonGroup
                    items={statusItems}
                    value={statusFilter}
                    handleChange={handleStatusChange}
                    className='flex items-start self-stretch mb-[var(--spacing-02)] w-[325px] bg-background-secondary border-none p-[var(--spacing-02)] [&_button]:border-none [&_button]:px-4 [&_button]:py-[6px] [&_button[aria-selected="true"]]:bg-background-primary [&_div]:after:hidden'
                />
            </div>
            <div className='flex justify-between items-center mb-[var(--spacing-07)]'>
                <div className='flex justify-between items-center max-w-[380px]'>
                    <Input
                        type='text'
                        placeholder='Search by name or email...'
                        value={searchTerm}
                        id='member-search'
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className='px-[var(--spacing-03)] w-[380px] border border-opaque rounded-[4px] h-[44px] bg-primary pr-[40px]'
                        rightIcon={
                            <label htmlFor='member-search' className='cursor-pointer'>
                                <MagnifyingGlassIcon style={{ marginLeft: '8px' }} />
                            </label>
                        }
                    />
                </div>
                <div className='flex flex-row items-center gap-[var(--spacing-03)] [&_.ant-input-outlined:focus]:shadow-none [&_.ant-input-outlined:focus-within]:border-opaque [&_.ant-input-outlined:focus-within]:shadow-none [&_.ant-input-outlined:focus-within]:bg-primary'>
                    {disableOperations ? (
                        <TooltipWrapperDisabled
                            component={
                                <Button
                                    variant={ButtonVariant.Secondary}
                                    className='flex items-center px-[var(--spacing-04)] py-[10px] gap-[4px] text-md [&_svg_path]:fill-inversePrimary disabled:border-opaque disabled:[&_svg_rect]:fill-opaque'
                                    onClick={handleResendInvites}
                                    disabled={!selectedMembers.length}
                                >
                                    <MailIcon />
                                    Resend Selected
                                </Button>
                            }
                        />
                    ) : (
                        <Button
                            variant={ButtonVariant.Secondary}
                            className='flex items-center px-[var(--spacing-04)] py-[10px] gap-[4px] text-md [&_svg_path]:fill-inversePrimary disabled:border-opaque disabled:[&_svg_rect]:fill-opaque'
                            onClick={handleResendInvites}
                            disabled={!selectedMembers.length}
                        >
                            <MailIcon />
                            Resend Selected
                        </Button>
                    )}
                </div>
            </div>
            <div className='overflow-x-auto [&_th]:font-medium'>
                <table className='w-full bg-primary border border-opaque border-collapse min-w-[950px]'>
                    <thead>
                        <tr className='border-b border-opaque h-[52px] [&_span]:font-medium [&>th]:font-normal [&>th]:text-left [&>th]:text-content-secondary [&>th]:bg-background-secondary [&>th:last-child]:text-right [&>td:last-child]:pr-[var(--spacing-07)] [&>th]:px-5 [&>th]:py-[14px] [&_.wrapper]:flex [&_.wrapper]:justify-between [&_.wrapper]:flex-row [&_.wrapper]:h-[52px] [&_.wrapper]:items-center [&_.wrapper]:p-[var(--spacing-04)]'>
                            <th>
                                {disableOperations ? (
                                    <TooltipWrapperDisabled component={<Checkbox disabled />} />
                                ) : (
                                    <Checkbox
                                        checked={isAllSelected()}
                                        onChange={handleSelectAll}
                                    />
                                )}
                            </th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Last Active / Invite Sent</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>{getContent()}</tbody>
                </table>
            </div>
        </div>
    );
};
